import type { TrackPermissions } from '../types/track';

/**
 * Maps a role string to track permissions
 * @param role - The user's role: 'owner', 'editor', 'viewer', 'musician', 'sound_engineer', 'content_maker'
 * @param isOwner - Whether the user is the track owner
 * @returns TrackPermissions object with granular permissions
 */
export function getTrackPermissions(role: string, isOwner: boolean): TrackPermissions {
  // Owner has all permissions
  if (isOwner || role === 'owner') {
    return {
      canRead: true,
      canUploadAudio: true,
      canUploadPromo: true,
      canAddAudioComments: true,
      canEditMetadata: true,
      canDelete: true,
      canShare: true,
    };
  }

  // Editor has most permissions except delete and share
  if (role === 'editor') {
    return {
      canRead: true,
      canUploadAudio: true,
      canUploadPromo: true,
      canAddAudioComments: true,
      canEditMetadata: true,
      canDelete: false,
      canShare: false,
    };
  }

  // Musician can upload audio and add comments, but not promo or metadata
  if (role === 'musician') {
    return {
      canRead: true,
      canUploadAudio: true,
      canUploadPromo: false,
      canAddAudioComments: true,
      canEditMetadata: false,
      canDelete: false,
      canShare: false,
    };
  }

  // Sound Engineer has same permissions as Musician
  if (role === 'sound_engineer') {
    return {
      canRead: true,
      canUploadAudio: true,
      canUploadPromo: false,
      canAddAudioComments: true,
      canEditMetadata: false,
      canDelete: false,
      canShare: false,
    };
  }

  // Content Maker can upload promo/video/footages but not audio or comments
  if (role === 'content_maker') {
    return {
      canRead: true,
      canUploadAudio: false,
      canUploadPromo: true,
      canAddAudioComments: false,
      canEditMetadata: false,
      canDelete: false,
      canShare: false,
    };
  }

  // Viewer (default) - read only
  return {
    canRead: true,
    canUploadAudio: false,
    canUploadPromo: false,
    canAddAudioComments: false,
    canEditMetadata: false,
    canDelete: false,
    canShare: false,
  };
}

